import { SEVERITIES } from '../../close-the-loop';
import type { ManageableReason, MySettings, QuietWindow, RealtimeChannel, Severity } from '../../close-the-loop';
import { maskPhone } from '../../../utilities/maskPhone';

export type AlertsReach =
  | { readonly kind: 'phone'; readonly channel: RealtimeChannel; readonly masked: string | null }
  | { readonly kind: 'appOnly' };

export interface ModeGroups {
  readonly now: readonly Severity[];
  readonly digest: readonly Severity[];
  readonly off: readonly Severity[];
}

export type DoneFix = 'verifyPhone' | 'askAdmin';

export const fixOf = (reason: ManageableReason): DoneFix => (reason === 'UNKNOWN_ROLE' ? 'askAdmin' : 'verifyPhone');

export interface DoneSummary {
  readonly incomplete: ManageableReason | null;
  readonly alerts: AlertsReach;
  readonly modes: ModeGroups;
  readonly morningEmail: boolean;
  readonly digestAtMinute: number;
  readonly quietHours: QuietWindow | null;
}

type SummarySource = Pick<
  MySettings,
  'reachesOn' | 'emailVerified' | 'manageableReason' | 'quiet' | 'quietHours' | 'modes' | 'allowed' | 'digestAtMinute'
>;

const choosableOf = (allowed: SummarySource['allowed']): readonly Severity[] => SEVERITIES.filter((severity) => allowed[severity].length > 1);

const modeGroupsOf = ({ modes, allowed }: SummarySource): ModeGroups => {
  const chosen = choosableOf(allowed);
  return {
    now: chosen.filter((severity) => modes[severity] === 'NOW'),
    digest: chosen.filter((severity) => modes[severity] === 'DIGEST'),
    off: chosen.filter((severity) => modes[severity] === 'OFF'),
  };
};

const quietApplies = ({ quiet }: SummarySource): boolean => quiet.active || quiet.startsAt !== null;

export const doneSummaryOf = (settings: SummarySource, number: string | null): DoneSummary => ({
  incomplete: settings.manageableReason,
  alerts:
    settings.reachesOn === null
      ? { kind: 'appOnly' }
      : { kind: 'phone', channel: settings.reachesOn, masked: number === null ? null : maskPhone(number) },
  modes: modeGroupsOf(settings),
  morningEmail: settings.emailVerified,
  digestAtMinute: settings.digestAtMinute,
  quietHours: quietApplies(settings) ? settings.quietHours : null,
});
